import { marked } from 'marked'
import sanitizeHtml from 'sanitize-html'
import type { HeatingSystem } from '~/shared/types/heatingsystem'

export const sanitizeContent = (content: string | undefined) => {
	if (content) {
		return sanitizeHtml(content, {
			allowedTags: ['a', 'br', 'strong', 'sub'],
			allowedAttributes: {
				a: ['href', 'class', 'target', 'rel'],
			},
		})
	}
	return content
}

export const convertNewlinesToBreaks = (string: string) => {
	if (!string) return string
	return string.replace(/\\n/g, '<br>')
}

export const formatAppTexts = (appTextRes: Array<{ key: string; content: string }> | null | undefined): Record<string, string> => {
	if (!appTextRes) return {}
	const data = appTextRes.filter((text) => text.key && text.content)

	const formatedData = data.reduce((acc: { [key: string]: string }, text) => {
		acc[text.key.trim()] = convertNewlinesToBreaks(text.content)
		return acc
	}, {})
	return formatedData
}

/**
 * Convert markdown link syntax of `[]()` to HTML `<a>` tag.
 * Automatically handle valid URLs and emails by prepending `mailto:` for email addresses if necessary.
 * Links must start with "https", otherwise return the input directly.
 */
export const markdownToHtml = (input: string | undefined) => {
	if (input) {
		const renderer = new marked.Renderer()
		renderer.link = ({ href, title, text }) => {
			if (!href.startsWith('https://')) {
				if (href.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
					if (!href.startsWith('mailto:')) {
						href = `mailto:${href}`
					}
				} else {
					return text
				}
			}
			const target = href.startsWith('mailto:') ? '' : ' target="_blank"'
			return `<a href="${href}"${target} class="underline">${text}</a>`
		}
		return marked.parseInline(input, { renderer, async: false })
	}
	return input
}

export const sanitizeTagsHTML = (content: string, allowedTags: string[]): string | undefined => {
	if (content) {
		const htmlContent = markdownToHtml(content) || ''
		return sanitizeHtml(htmlContent, {
			allowedTags: ['a', ...allowedTags],
			allowedAttributes: {
				a: ['href', 'class', 'target', 'rel'],
			},
		})
	}
}

const DWH_HEILAST_PLACEHOLDER = /{{hl}}/ // heizlast
const DWH_AMPEL_DETAILS_WP_PLACEHOLDER = /^{{Text Ampel Details rot und\/oder gelb}}$/
const DWH_FERNWAERME_BETREIBER_PLACEHOLDER = /{{Betreiber}}/
const DWH_PRODUCT_DB_LINK_PLACEHOLDER = /{{link_product_db}}/
const DWH_MAX_PEGEL_PLACEHOLDER = /{{max_pegel {2}aus REST 31}}/
const DWH_FW_DISTANZ_PLACEHOLDER = /{{"Distanz FW\/NW" aus Potenzialanalyse}}/

const TEXT_VALUE_PLACEHOLDER = '_'

const replaceHeizlastPlaceholder = (text: string, system: HeatingSystem) => {
	if (!DWH_HEILAST_PLACEHOLDER.test(text)) {
		return text
	}
	return text.replace(
		DWH_HEILAST_PLACEHOLDER,
		system.heizlast !== null ? Math.round(system.heizlast).toString() : TEXT_VALUE_PLACEHOLDER
	)
}

const replaceAmpelDetailsPlaceholder = (text: string, system: HeatingSystem) => {
	if (!DWH_AMPEL_DETAILS_WP_PLACEHOLDER.test(text)) {
		return text
	}
	return text.replace(
		DWH_AMPEL_DETAILS_WP_PLACEHOLDER,
		system.heizlast !== null ? Math.round(system.heizlast).toString() : TEXT_VALUE_PLACEHOLDER
	)
}

const replaceFernwaermeBetreiber = (text: string, system: HeatingSystem) => {
	if (!isFernwaermeHeatingSystem(system.E_ID) || !DWH_FERNWAERME_BETREIBER_PLACEHOLDER.test(text)) {
		return text
	}

	return text.replace(
		DWH_FERNWAERME_BETREIBER_PLACEHOLDER,
		system.fwBetreiber && system.fwBetreiber[system.E_ID].betreiber !== undefined
			? `${system.fwBetreiber[system.E_ID].betreiber}, ${system.fwBetreiber[system.E_ID].email}`
			: TEXT_VALUE_PLACEHOLDER
	)
}

const replaceProductGetDatabaseLink = (text: string, system: HeatingSystem) => {
	if (!DWH_PRODUCT_DB_LINK_PLACEHOLDER.test(text) || !system.link_product_db) {
		return text
	}

	return text.replace(
		DWH_PRODUCT_DB_LINK_PLACEHOLDER,
		system.link_product_db !== undefined ? `${system.link_product_db}` : TEXT_VALUE_PLACEHOLDER
	)
}

const replacMaxPegel = (text: string, system: HeatingSystem) => {
	if (!DWH_MAX_PEGEL_PLACEHOLDER.test(text) || !system.maxPegel) {
		return text
	}

	return text.replace(
		DWH_MAX_PEGEL_PLACEHOLDER,
		system.maxPegel !== undefined ? `${system.maxPegel}` : TEXT_VALUE_PLACEHOLDER
	)
}

const replaceFwDistanzPlaceholder = (text: string, system: HeatingSystem) => {
	if (!DWH_FW_DISTANZ_PLACEHOLDER.test(text) || !system.fwDistanz) {
		return text
	}

	return text.replace(
		DWH_FW_DISTANZ_PLACEHOLDER,
		system.fwDistanz !== undefined ? `${system.fwDistanz}` : TEXT_VALUE_PLACEHOLDER
	)
}

export const replacePlaceholder = (text: string | null, system: HeatingSystem) => {
	if (!text) {
		return ''
	}
	const placeholderReplacers = [
		replaceHeizlastPlaceholder,
		replaceAmpelDetailsPlaceholder,
		replaceFernwaermeBetreiber,
		replaceProductGetDatabaseLink,
		replacMaxPegel,
		replaceFwDistanzPlaceholder,
	]

	return placeholderReplacers.reduce((updatedText, replacer) => replacer(updatedText, system), text)
}

export const getDetailedClarificationText = (system: HeatingSystem): string => {
	switch (system.classification) {
		case 'possible':
			return system.detT_gruen
		case 'clarify':
			if (isFernwaermeHeatingSystem(system.E_ID)) return system.detT_BDgelb

			if (system.bedarfAmpel === 'gelb') return system.detT_BDgelb
			if (system.bedarfAmpel === 'orange' || system.bedarfAmpel === 'rot') return system.detT_BDrot

			// default if bedarfAmpel equal null
			return system.detT_BDleer
		case 'unsuitable':
			// wont be shown anywhere as unsuitable currently does not have openable details
			return system.detT_BDrot ?? ''
		default:
			return 'Aufgrund fehlender Daten ist keine spezifische Aussage zu Ihrem Grundstück möglich.'
	}
}
